import { useCallback, useEffect, useRef, useState } from 'react'

import { documentsApi } from '@/lib/api'
import { noteEmailUnverified } from '@/layouts/verificationGate'

import {
  classifyUploadError,
  makeItem,
  queueSummary,
  rowMessage,
  stopsQueue,
  type UploadItem,
  type UploadRefusal,
} from './uploadQueue'

/**
 * The upload queue: files in, one request at a time, out.
 *
 * **Strictly sequential.** The gateway holds five upload slots per instance
 * (`uploadSem` in `api/internal/api/handlers.go`) and answers 503 past them,
 * so a drop of twenty files fired at once loses some — which is exactly what
 * the Flutter dialog does (`debugAddFile` starts every file's upload the
 * moment it is ingested). This pumps one at a time and waits.
 *
 * It does not call `documentsApi.uploadAll`, and that is a deliberate
 * departure, not an oversight: `uploadAll` is the same `for`/`await` loop but
 * it **throws on the first failure**, dropping every file behind it, and it
 * reports progress only per completed file. This screen has to show a
 * per-file bar and let one file fail without taking the rest with it. The
 * serialisation — the part that matters — is identical.
 *
 * The authoritative list lives in a ref because the pump walks it inside an
 * async loop; React state is a mirror for rendering.
 */
export interface UploadQueue {
  items: UploadItem[]
  /** Set when an account-wide refusal stopped the queue. */
  blocked: UploadRefusal | null
  add: (files: FileList | File[]) => void
  /** Retries one failed file. */
  retry: (id: string) => void
  remove: (id: string) => void
  /** Sends the files that were blocked — after confirming an address, say. */
  resume: () => void
  clear: () => void
}

export function useUploadQueue(options: {
  templateId: string
  maxSizeMb: number
  /** Called once per file that landed, with the id the gateway assigned. */
  onUploaded?: (documentId: string, item: UploadItem) => void
  /** Called when the queue has drained (nothing queued or in flight). */
  onDrained?: () => void
}): UploadQueue {
  const { templateId, maxSizeMb, onUploaded, onDrained } = options

  const itemsRef = useRef<UploadItem[]>([])
  const [items, setItems] = useState<UploadItem[]>([])
  const [blocked, setBlocked] = useState<UploadRefusal | null>(null)
  const blockedRef = useRef<UploadRefusal | null>(null)
  const pumping = useRef(false)
  const alive = useRef(true)

  // Latest callbacks without re-creating the pump on every render.
  const handlers = useRef({ onUploaded, onDrained })
  handlers.current = { onUploaded, onDrained }

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const sync = useCallback(() => {
    setItems([...itemsRef.current])
  }, [])

  const patch = useCallback(
    (id: string, changes: Partial<UploadItem>) => {
      itemsRef.current = itemsRef.current.map((item) =>
        item.id === id ? { ...item, ...changes } : item,
      )
      if (alive.current) sync()
    },
    [sync],
  )

  const pump = useCallback(async () => {
    if (pumping.current) return
    pumping.current = true
    try {
      for (;;) {
        if (!alive.current || blockedRef.current) break
        const next = itemsRef.current.find((item) => item.status === 'queued')
        if (!next) break

        patch(next.id, { status: 'uploading', progress: 0, error: undefined })
        try {
          const documentId = await documentsApi.upload(
            templateId,
            next.file,
            next.name,
            (sent, total) => {
              if (total > 0) patch(next.id, { progress: Math.min(1, sent / total) })
            },
          )
          if (!alive.current) break
          patch(next.id, { status: 'done', progress: 1, documentId })
          handlers.current.onUploaded?.(documentId, next)
        } catch (error) {
          if (!alive.current) break
          const refusal = classifyUploadError(error)
          // The shell owns the verification banner; it learns about the
          // pause from whoever caught the refusal, which is here.
          noteEmailUnverified(error)
          patch(next.id, { status: 'failed', error: rowMessage(refusal) })
          if (stopsQueue(refusal)) {
            blockedRef.current = refusal
            setBlocked(refusal)
            // Everything still waiting is held, not dropped: the person can
            // resume after topping up or confirming their address.
            itemsRef.current = itemsRef.current.map((item) =>
              item.status === 'queued' ? { ...item, status: 'blocked' } : item,
            )
            sync()
            break
          }
        }
      }
    } finally {
      pumping.current = false
      if (alive.current) handlers.current.onDrained?.()
    }
  }, [patch, sync, templateId])

  const add = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files as ArrayLike<File>)
      if (list.length === 0) return
      itemsRef.current = [...itemsRef.current, ...list.map((file) => makeItem(file, maxSizeMb))]
      sync()
      void pump()
    },
    [maxSizeMb, pump, sync],
  )

  const retry = useCallback(
    (id: string) => {
      patch(id, { status: 'queued', progress: 0, error: undefined })
      void pump()
    },
    [patch, pump],
  )

  const remove = useCallback(
    (id: string) => {
      itemsRef.current = itemsRef.current.filter((item) => item.id !== id)
      sync()
    },
    [sync],
  )

  const resume = useCallback(() => {
    blockedRef.current = null
    setBlocked(null)
    itemsRef.current = itemsRef.current.map((item) =>
      item.status === 'blocked' || item.status === 'failed'
        ? { ...item, status: 'queued', progress: 0, error: undefined }
        : item,
    )
    sync()
    void pump()
  }, [pump, sync])

  const clear = useCallback(() => {
    itemsRef.current = []
    blockedRef.current = null
    setBlocked(null)
    sync()
  }, [sync])

  return { items, blocked, add, retry, remove, resume, clear }
}

export { queueSummary }
